import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';

import { ref } from 'Util/CustomPropTypes';

import { VIDEO_STATE } from '../constant';

import { VideoPlayerContext } from './context';

const initialState = {
  playerRef: null,
  userActive: true,
  videoState: VIDEO_STATE.LOAD,
};

const ACTION_TYPES = {
  RESET: 'RESET',
  SET_VIDEO_PLAYER: 'SET_VIDEO_PLAYER',
  SET_USER_STATE: 'SET_USER_STATE',
  SET_VIDEO_STATE: 'SET_VIDEO_STATE',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.RESET:
      return initialState;

    case ACTION_TYPES.SET_VIDEO_PLAYER:
      return action?.payload ? { ...state, playerRef: action.payload } : initialState;

    case ACTION_TYPES.SET_USER_STATE:
      return { ...state, userActive: action.payload };

    case ACTION_TYPES.SET_VIDEO_STATE: {
      const newVideoState = Object.values(VIDEO_STATE).includes(action?.payload)
        ? action.payload
        : VIDEO_STATE.LOAD;
      return {
        ...state,
        videoState: newVideoState,
      };
    }

    default:
      throw new Error(
        `Unhandle action types ${action.type},
        \n please use one of these type ${JSON.stringify(Object.values(ACTION_TYPES))}`
      );
  }
};

const VideoPlayerProvider = ({ children }) => {
  const [videoPlayerContext, dispatch] = useReducer(reducer, initialState);
  const { playerRef, videoState, userActive } = videoPlayerContext;
  const player = playerRef?.player ?? null;

  const setPlayer = (payload) => dispatch({ type: ACTION_TYPES.SET_VIDEO_PLAYER, payload });
  const setUserActive = (payload) => dispatch({ type: ACTION_TYPES.SET_USER_STATE, payload });
  const setVideoState = (payload) => dispatch({ type: ACTION_TYPES.SET_VIDEO_STATE, payload });
  const reset = () => dispatch({ type: ACTION_TYPES.RESET });

  useEffect(() => {
    if (player && typeof player.on === 'function') {
      player.on('useractive', () => {
        setUserActive(true);
      });
      player.on('userinactive', () => {
        setUserActive(false);
      });
      player.on('ready', () => {
        setVideoState(VIDEO_STATE.READY);
      });
      player.on('play', () => {
        setVideoState(VIDEO_STATE.PLAY);
      });
      player.on('ended', () => {
        setVideoState(VIDEO_STATE.ENDED);
      });
      player.on('pause', () => {
        setVideoState(VIDEO_STATE.PAUSED);
      });
      player.on('dispose', () => {
        reset();
      });
    }
  }, [player]);

  const play = typeof playerRef?.playVideo === 'function' ? playerRef.playVideo : () => {};
  const pause = typeof playerRef?.pauseVideo === 'function' ? playerRef.pauseVideo : () => {};

  return (
    <VideoPlayerContext.Provider
      value={{
        player: player,
        playerRef: playerRef,
        play: play,
        pause: pause,
        replay: play,
        setPlayer: setPlayer,
        userActive: userActive,
        setUserActive: setUserActive,
        videoState: videoState,
        VIDEO_STATE: VIDEO_STATE,

        // TODO: do we need player.dispose()?
        // See https://docs.videojs.com/tutorial-player-workflows.html#removing-players
      }}
    >
      {children}
    </VideoPlayerContext.Provider>
  );
};

const VideoPlayerContextPropTypes = PropTypes.shape({
  playerRef: ref,
  videoState: PropTypes.oneOf(Object.values(VIDEO_STATE)),
  userActive: PropTypes.bool,
});

export { VideoPlayerContextPropTypes };

export default VideoPlayerProvider;
